import { useEffect, useState, useCallback } from "react";
import {
  Search, Plus, Eye, UserCheck, UserX, ShieldOff, KeyRound,
  Trash2, ChevronLeft, ChevronRight, Unlock
} from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import PageHeader from "@/components/admin/PageHeader";
import StatusBadge from "@/components/admin/StatusBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import AdminDetailDrawer from "@/components/admin/AdminDetailDrawer";

const PAGE_SIZE = 20;

const EMPTY_FORM = { name: "", email: "", phone: "", department: "", status: "active" };

export default function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [detailAdmin, setDetailAdmin] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/admin/management/admins", { params });
      setAdmins(data.admins);
      setTotal(data.total);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  // ── Create Admin ──────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await api.post("/admin/management/admins", form);
      toast.success("Admin created and welcome email sent.");
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      loadAdmins();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setFormLoading(false);
    }
  };

  // ── Edit Admin ────────────────────────────────────────────────────────────
  const openEdit = (admin) => {
    setEditAdmin(admin);
    setForm({
      name: admin.name || "",
      phone: admin.phone || "",
      department: admin.department || "",
      status: admin.status || "active",
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {};
      if (form.name) payload.name = form.name;
      if (form.phone !== undefined) payload.phone = form.phone || null;
      if (form.department !== undefined) payload.department = form.department || null;
      if (form.status) payload.status = form.status;
      await api.patch(`/admin/management/admins/${editAdmin.user_id}`, payload);
      toast.success("Admin updated.");
      setEditAdmin(null);
      loadAdmins();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setFormLoading(false);
    }
  };

  // ── Status actions ────────────────────────────────────────────────────────
  const doAction = async (url, successMsg) => {
    try {
      await api.post(url);
      toast.success(successMsg);
      loadAdmins();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doDelete = async (adminId) => {
    try {
      await api.delete(`/admin/management/admins/${adminId}`);
      toast.success("Admin removed.");
      loadAdmins();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doResetPassword = async (adminId) => {
    try {
      await api.post(`/admin/management/admins/${adminId}/reset-password`);
      toast.success("Password reset. Email sent to admin.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminGuard requireSuperAdmin>
      <AdminLayout>
        <PageHeader
          title="Admin Management"
          subtitle={`${total} admin accounts`}
          actions={
            <Button
              size="sm"
              onClick={() => { setForm(EMPTY_FORM); setCreateOpen(true); }}
              className="bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/90 font-heading uppercase tracking-wider text-xs rounded-full h-9 px-5"
            >
              <Plus size={14} className="mr-1.5" /> Add Admin
            </Button>
          }
        />

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-4 mb-6 flex flex-wrap gap-3 items-center shadow-sm">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--zt-charcoal))]/40" />
            <input
              type="text"
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 h-9 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/50 font-body"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-xs border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/50 font-heading uppercase tracking-wider"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[800px]">
              <thead>
                <tr className="border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40">
                  {["Name / Email", "Role", "Department", "Status", "Last Login", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3.5 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-b border-[hsl(var(--zt-beige-deep))]/50 animate-pulse">
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-4 py-4"><div className="h-4 bg-[hsl(var(--zt-beige-deep))]/50 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : admins.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center text-sm text-[hsl(var(--zt-charcoal))]/40">
                      No admin accounts found
                    </td>
                  </tr>
                ) : admins.map((a) => (
                  <tr key={a.user_id} className="border-b border-[hsl(var(--zt-beige-deep))]/50 hover:bg-[hsl(var(--zt-beige))]/30 transition-colors">
                    <td className="px-4 py-3.5">
                      <p className="font-semibold text-sm text-[hsl(var(--zt-teal))]">{a.name || "—"}</p>
                      <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55 mt-0.5">{a.email}</p>
                      {a.account_locked && (
                        <span className="text-[10px] text-red-500 font-heading uppercase tracking-wider">🔒 Locked</span>
                      )}
                      {a.must_change_password && (
                        <span className="text-[10px] text-amber-600 font-heading uppercase tracking-wider ml-1">⚠ Must change pwd</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5"><StatusBadge status={a.role} /></td>
                    <td className="px-4 py-3.5 text-sm text-[hsl(var(--zt-charcoal))]/70">{a.department || "—"}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={a.status || "active"} /></td>
                    <td className="px-4 py-3.5 text-[11px] text-[hsl(var(--zt-charcoal))]/55 whitespace-nowrap">{fmtDate(a.last_login_at)}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {/* View */}
                        <ActionBtn title="View details" color="teal" onClick={() => setDetailAdmin(a.user_id)}>
                          <Eye size={12} />
                        </ActionBtn>
                        {/* Edit */}
                        <ActionBtn title="Edit" color="blue" onClick={() => openEdit(a)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </ActionBtn>
                        {/* Activate/Deactivate */}
                        {a.status !== "active" ? (
                          <ActionBtn title="Activate" color="green" onClick={() => doAction(`/admin/management/admins/${a.user_id}/activate`, "Admin activated.")}>
                            <UserCheck size={12} />
                          </ActionBtn>
                        ) : (
                          <ActionBtn title="Deactivate" color="amber" onClick={() => doAction(`/admin/management/admins/${a.user_id}/deactivate`, "Admin deactivated.")}>
                            <UserX size={12} />
                          </ActionBtn>
                        )}
                        {/* Suspend */}
                        {a.status !== "suspended" && (
                          <ConfirmActionBtn
                            title="Suspend"
                            color="orange"
                            description={`Suspend ${a.name || a.email}? They will be immediately logged out.`}
                            onConfirm={() => doAction(`/admin/management/admins/${a.user_id}/suspend`, "Admin suspended.")}
                          >
                            <ShieldOff size={12} />
                          </ConfirmActionBtn>
                        )}
                        {/* Unlock */}
                        {a.account_locked && (
                          <ActionBtn title="Unlock account" color="purple" onClick={() => doAction(`/admin/management/admins/${a.user_id}/unlock`, "Account unlocked.")}>
                            <Unlock size={12} />
                          </ActionBtn>
                        )}
                        {/* Reset password */}
                        {a.role !== "super_admin" && (
                          <ConfirmActionBtn
                            title="Reset Password"
                            color="indigo"
                            description={`Reset password for ${a.name || a.email}? A new temporary password will be emailed.`}
                            onConfirm={() => doResetPassword(a.user_id)}
                          >
                            <KeyRound size={12} />
                          </ConfirmActionBtn>
                        )}
                        {/* Delete */}
                        {a.role !== "super_admin" && (
                          <ConfirmActionBtn
                            title="Delete"
                            color="red"
                            description={`Permanently remove ${a.name || a.email}? This action cannot be undone.`}
                            onConfirm={() => doDelete(a.user_id)}
                          >
                            <Trash2 size={12} />
                          </ConfirmActionBtn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-[hsl(var(--zt-beige-deep))]">
              <p className="text-xs text-[hsl(var(--zt-charcoal))]/50 font-heading">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40 hover:bg-[hsl(var(--zt-beige))] transition-colors">
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs font-heading font-semibold text-[hsl(var(--zt-teal))]">{page} / {totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40 hover:bg-[hsl(var(--zt-beige))] transition-colors">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Create Admin Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-[0.15em] text-[hsl(var(--zt-teal))] text-base">
                Create New Admin
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-2">
              <AdminFormFields form={form} setForm={setForm} isCreate />
              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading}
                  className="bg-[hsl(var(--zt-teal))] text-white rounded-full hover:bg-[hsl(var(--zt-teal))]/90">
                  {formLoading ? "Creating…" : "Create Admin"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Admin Dialog */}
        <Dialog open={!!editAdmin} onOpenChange={(o) => !o && setEditAdmin(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-[0.15em] text-[hsl(var(--zt-teal))] text-base">
                Edit Admin
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4 pt-2">
              <AdminFormFields form={form} setForm={setForm} isCreate={false} />
              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={() => setEditAdmin(null)} className="rounded-full">
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading}
                  className="bg-[hsl(var(--zt-teal))] text-white rounded-full hover:bg-[hsl(var(--zt-teal))]/90">
                  {formLoading ? "Saving…" : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Detail Drawer */}
        {detailAdmin && (
          <AdminDetailDrawer adminId={detailAdmin} onClose={() => setDetailAdmin(null)} />
        )}
      </AdminLayout>
    </AdminGuard>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function ActionBtn({ title, color, onClick, children }) {
  const colors = {
    teal:   "bg-[hsl(var(--zt-beige))] text-[hsl(var(--zt-teal))]/60 hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige-deep))]",
    blue:   "bg-blue-50 text-blue-500 hover:bg-blue-100",
    green:  "bg-emerald-50 text-emerald-600 hover:bg-emerald-100",
    amber:  "bg-amber-50 text-amber-600 hover:bg-amber-100",
    orange: "bg-orange-50 text-orange-600 hover:bg-orange-100",
    purple: "bg-purple-50 text-purple-600 hover:bg-purple-100",
    indigo: "bg-indigo-50 text-indigo-600 hover:bg-indigo-100",
    red:    "bg-red-50 text-red-500 hover:bg-red-100",
  };
  return (
    <button onClick={onClick} title={title}
      className={`w-7 h-7 rounded-lg grid place-items-center transition-colors ${colors[color] || colors.teal}`}>
      {children}
    </button>
  );
}

function ConfirmActionBtn({ title, color, description, onConfirm, children }) {
  const colors = {
    orange: "bg-orange-50 text-orange-600 hover:bg-orange-100",
    indigo: "bg-indigo-50 text-indigo-600 hover:bg-indigo-100",
    red:    "bg-red-50 text-red-500 hover:bg-red-100",
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button title={title}
          className={`w-7 h-7 rounded-lg grid place-items-center transition-colors ${colors[color] || colors.red}`}>
          {children}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}?</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}
            className={color === "red" ? "bg-red-500 hover:bg-red-600" : "bg-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-teal))]/90"}>
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AdminFormFields({ form, setForm, isCreate }) {
  const f = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const inputCls = "w-full h-9 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/40 font-body";
  const labelCls = "block text-xs font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1";
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className={labelCls}>Full Name *</label>
          <input className={inputCls} value={form.name} onChange={f("name")} required />
        </div>
        {isCreate && (
          <div className="col-span-2">
            <label className={labelCls}>Email *</label>
            <input className={inputCls} type="email" value={form.email} onChange={f("email")} required />
          </div>
        )}
        <div>
          <label className={labelCls}>Phone</label>
          <input className={inputCls} value={form.phone || ""} onChange={f("phone")} />
        </div>
        <div>
          <label className={labelCls}>Department</label>
          <input className={inputCls} value={form.department || ""} onChange={f("department")} />
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Status</label>
          <select className={inputCls} value={form.status} onChange={f("status")}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            {!isCreate && <option value="suspended">Suspended</option>}
          </select>
        </div>
      </div>
      {isCreate && (
        <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/50 mt-1 font-body">
          A secure temporary password will be auto-generated and emailed to the admin.
          They must change it on first login.
        </p>
      )}
    </>
  );
}

function fmtDate(d) {
  if (!d) return "Never";
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
